/**
 * 年末調整 書類キー定数。
 * ドロワーでの書類→タスク対応は instanceDocuments エンドポイントが返す
 * itemKey ベースのレスポンスで解決する。
 *
 * この定数は ProgressCell・タブ・ドロワーなど一覧画面側で使用する。
 * スキーマ・wire 型・書類キー配列はすべて published `@standandforce/api-contract`
 * を参照し、FE 側では書類リストをリテラル定義しない。
 */

import { type CountYearEndAdjustmentInstancesByTagDocumentTabsOutput, type ListYearEndAdjustmentInstancesByTagRowOutput, yearEndAdjustmentDocumentItemKeyValues, type YearEndAdjustmentDocumentStatusSchema, type YearEndAdjustmentOverallStatusSchema } from '@standandforce/api-contract';
import type { z } from 'zod';

/** 書類の5状態（published contract の enum から導出） */
export type YearEndAdjustmentDocumentStatus = z.infer<typeof YearEndAdjustmentDocumentStatusSchema>;

/** インスタンス全体の4状態（published contract の enum から導出） */
export type YearEndAdjustmentOverallStatus = z.infer<typeof YearEndAdjustmentOverallStatusSchema>;

/**
 * 年末調整 6 書類のキー（表示順＝カタログ順）。
 *
 * BE の `yearEndAdjustmentDocumentItemKeyValues`（= ドメインカタログ
 * `YEAR_END_ADJUSTMENT_DOCUMENT_ITEM_CATALOG` の写し）を published パッケージ経由で
 * そのまま再エクスポートする。FE では書類リストを重複定義しない。
 * 画面が前提とするキー・順序が想定どおりであることは `documentSections.test.ts` で固定する。
 */
export const YEAR_END_ADJUSTMENT_DOCUMENT_SECTION_KEYS = yearEndAdjustmentDocumentItemKeyValues;
export type YearEndAdjustmentDocumentSectionKey = (typeof YEAR_END_ADJUSTMENT_DOCUMENT_SECTION_KEYS)[number];

/**
 * 任意の文字列が既知の書類キーかを判定する型ガード。
 * ドロワー書類 API の `itemKey` は wire 上 `string`（BE 先行ロールアウト耐性のための緩い型）
 * なので、selector マップを引く前に既知キーへ絞り込むために使う。未知キー（将来 BE が
 * 先行追加した書類等）は false を返し、呼び出し側で生キー表示等にフォールバックする。
 */
export function isYearEndAdjustmentDocumentSectionKey(key: string): key is YearEndAdjustmentDocumentSectionKey {
  return (YEAR_END_ADJUSTMENT_DOCUMENT_SECTION_KEYS as readonly string[]).includes(key);
}
export function getYearEndAdjustmentDocumentStatusByItemKey(row: Pick<ListYearEndAdjustmentInstancesByTagRowOutput, 'items'>, itemKey: YearEndAdjustmentDocumentSectionKey): YearEndAdjustmentDocumentStatus | undefined {
  return row.items.find(item => item.itemKey === itemKey)?.status;
}
export function getYearEndAdjustmentDocumentTabCountByItemKey(tabCounts: Pick<CountYearEndAdjustmentInstancesByTagDocumentTabsOutput, 'items'>, itemKey: YearEndAdjustmentDocumentSectionKey): number {
  return tabCounts.items.find(item => item.itemKey === itemKey)?.count ?? 0;
}

/**
 * 「別途対応」フラグ（`separateHandling === true`）が立っている書類キーを
 * カタログ順（`YEAR_END_ADJUSTMENT_DOCUMENT_SECTION_KEYS`）で返す。
 */
export function getYearEndAdjustmentSeparateHandlingDocuments(row: Pick<ListYearEndAdjustmentInstancesByTagRowOutput, 'items'>): YearEndAdjustmentDocumentSectionKey[] {
  return YEAR_END_ADJUSTMENT_DOCUMENT_SECTION_KEYS.filter(itemKey => row.items.some(item => item.itemKey === itemKey && item.separateHandling === true));
}